import { useCallback, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Z_INDEX } from '../../../styles';
import type { OptionalProperties } from '../../../utils';
import { Flex } from '../../Core';
import { PopperHeader, type PopperHeaderProps } from '../../PopperHeader/PopperHeader';
import { Portal } from '../../Portal';
import type { UseDropdownPopperOutput, UseDropdownPopperProps } from '../Dropdown/useDropdownPopper';
import type { FormControlSizes } from '../types';
import { DropdownContext, dropdownContextDefaultValue } from './DropdownContext';
import { DropdownContentWrapper } from './styles';
import { DROPDOWN_PORTAL_ID } from './tokens';

export interface DropdownProps extends OptionalProperties<UseDropdownPopperOutput, 'dropdownContentRef'> {
  maxHeight?: number;
  comboboxStyles?: CSSProperties;
  menuStyles?: CSSProperties;
  size?: FormControlSizes;
  style?: CSSProperties;
  id?: string;
  children: ReactNode;
  /**  Whether or not to portalize the dropdown. Portalizing a DOM element places it at the root of the page,
   * meaning that it will be detached from the sibling elements and rendered above.
   * Used when there are annoying issues with overflowing / overlapping elements.
   * Setting this to true can cause positioning issues if the reference element moves around a lot or is animated on entry for example.
   */
  portalize?: boolean;
  isOpen: boolean;

  onTabOut?: (event: KeyboardEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;

  /**
   * Whether or not this dropdown is a parent to other "child" dropdowns within it.
   * This option influences the zIndex applied to make sure that portalized child dropdowns render
   * on top of this parent dropdown.
   */
  isParentDropdown?: boolean;

  headerProps?: PopperHeaderProps;
}

// Expose a subset of the props for now
// also cast the <T> to any since we're not using it
export interface ExposedDropdownProps
  extends
    Pick<DropdownProps, 'portalize' | 'maxHeight'>,
    Pick<UseDropdownPopperProps, 'dropdownPlacement' | 'dropdownWidth'> {}

export function Dropdown({
  menuStyles,
  isOpen,
  size,
  children,
  portalize = false,
  referenceWidth,
  dropdownWidth,
  setPopperElement,
  popper,
  dropdownContentRef,
  onTabOut,
  onKeyDown,
  isParentDropdown,
  id,
  dropdownHeight,
  maxHeight,

  headerProps,
}: DropdownProps) {
  // We expose keydown events to parents here through the hooks.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (onTabOut && event.key === 'Tab') {
        onTabOut(event);
      }

      onKeyDown && onKeyDown(event);
    },
    [onTabOut, onKeyDown]
  );

  const { attributes, styles } = popper;
  const minWidth = getDropdownMinWidth(referenceWidth, dropdownWidth);

  const dropdownContent = (
    <DropdownContentWrapper
      onKeyDown={handleKeyDown}
      {...attributes.popper}
      style={{
        ...styles.popper,
        minWidth,
        width: dropdownWidth,
        visibility: isOpen ? 'visible' : 'hidden',
        ...menuStyles,
        zIndex: isParentDropdown ? Z_INDEX.parentDropdown : Z_INDEX.dropdown,
      }}
      size={size}
      ref={setPopperElement}
      id={id}
      data-testid={`dropdown-content-wrapper-${isOpen ? 'visible' : 'hidden'}`}
      data-property-id={id ?? 'NO_VALUE_SET'}
    >
      {/* We add this context here to allow components inside a dropdown to know that they are being rendered inside of a dropdown.
            Defining this as a context instead of a prop to each component allows us to just do this once.
            Components then check to see if they are within this context and can use that information to do things such as modify their z-index to render on top of the dropdown.
         */}
      <DropdownContext.Provider value={dropdownContextDefaultValue}>
        <Flex
          flexDirection="column"
          background="backgroundDropdown"
          h={dropdownHeight}
          maxHeight={maxHeight}
          ref={dropdownContentRef}
        >
          {headerProps && <PopperHeader {...headerProps} />}
          {children}
        </Flex>
      </DropdownContext.Provider>
    </DropdownContentWrapper>
  );

  return (
    <>
      {portalize ? (
        <Portal portalId={DROPDOWN_PORTAL_ID} createTopLevelPortal={true}>
          {dropdownContent}
        </Portal>
      ) : (
        dropdownContent
      )}{' '}
    </>
  );
}

function getDropdownMinWidth(
  referenceWidth: DropdownProps['referenceWidth'],
  dropdownWidth: DropdownProps['dropdownWidth']
) {
  // Use the cached reference width from useDropdownPopper instead of reading
  // referenceElement.offsetWidth during render. Geometry reads in render can
  // force the browser to synchronously flush style/layout on dropdown open.
  const referenceMinWidth = referenceWidth == null ? undefined : `${referenceWidth}px`;
  const dropdownMinWidth = typeof dropdownWidth === 'number' ? `${dropdownWidth}px` : dropdownWidth;

  if (referenceMinWidth == null) {
    return undefined;
  }

  return dropdownMinWidth ? `min(${dropdownMinWidth}, ${referenceMinWidth})` : referenceMinWidth;
}
